import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
  Provider,
} from '@angular/core';
import { KommissionierungAddOn } from '@softapps/lager/kommissionierung/api';
import {
  ApplicationStore,
  getModuleGroupByName,
  registerAddOn,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import {
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SoftlineEntityService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import {
  provideSoftAppsModule,
  withLazySoftAppsModuleTranslation,
  withSoftAppsModuleInfo,
  withSoftAppsModuleRoutes,
} from '@softapps/core';
import { default as de } from '../i18n/de.json';
import { default as deTitles } from '../i18n/de.titles.json';
import { provideLagerkorrekturArtikelGlobals } from './lagerkorrektur-artikel.globals';
import { lagerkorrekturArtikelRoutes } from './lagerkorrektur-artikel.routes';
import { LagerkorrekturKommissionierungAddon } from './add-ons/kommissionierung.addon';
import { SOFTLINE_API_UMBUCHUNGSARTIKEL } from './lagerkorrektur-artikel.api';
import {
  SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
  SOFTLINE_FEATURE_UMBUCHUNGSARTIKEL,
  SOFTLINE_PERMISSION_LAGERKORREKTUR_ARTIKEL,
} from './lagerkorrektur-artikel.shared';
import { LagerkorrekturArtikelService } from './services/lagerkorrektur-artikel.service';
import { LagerkorrekturArtikelStore, UmbuchungsartikelStore } from './store';

export const lagerkorrekturArtikelProviders: (Provider | EnvironmentProviders)[] = [
  LagerkorrekturArtikelService,
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
      feature: LagerkorrekturArtikelStore.feature,
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_UMBUCHUNGSARTIKEL,
      feature: UmbuchungsartikelStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service) =>
            new SoftlineEntityService(service, SOFTLINE_API_UMBUCHUNGSARTIKEL),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    })
  ),
  provideEnvironmentInitializer(() => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'lagerkorrekturArtikel',
      language: 'de',
      translations: de,
    });
    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: '#LAGERKORREKTUR_ARTIKEL.APP',
      description: '#LAGERKORREKTUR_ARTIKEL.APP_DESC',
      route: '/lagerkorrektur-artikel',
      icon: 'fa-regular fa-circle-minus',
      permission: SOFTLINE_PERMISSION_LAGERKORREKTUR_ARTIKEL,
      group: getModuleGroupByName('Lager'),
    });
  }),
  registerAddOn(KommissionierungAddOn, LagerkorrekturKommissionierungAddon),
];

export function provideLagerkorrekturArtikelModule() {
  return [
    provideSoftAppsModule(
      {
        name: 'lagerkorrekturArtikel',
        title: '#LAGERKORREKTUR_ARTIKEL.APP',
        description: '#LAGERKORREKTUR_ARTIKEL.APP_DESC',
        icon: 'fa-regular fa-circle-minus',
        route: '/lagerkorrektur-artikel',
        permission: SOFTLINE_PERMISSION_LAGERKORREKTUR_ARTIKEL,
        group: getModuleGroupByName('Lager'),
      },
      withSoftAppsModuleRoutes({ app: lagerkorrekturArtikelRoutes }),
      withLazySoftAppsModuleTranslation({
        eager: { de: deTitles },
        lazy: { de: () => import('../i18n/de.json') },
      }),
      withSoftAppsModuleInfo({
        library: '@softapps/lager/lagerkorrektur-artikel',
        description: 'Lagerkorrektur je Artikel.',
      })
    ),
    ...provideLagerkorrekturArtikelGlobals(),
  ];
}
